import { buildQuery } from '@pn/core/domain/query';
import type { ApiDataSource } from '@pn/core/domain/workspace';
import { ApiError } from '@pn/core/errors';
import type { IDataProvider } from '@pn/core/services/data/ports';
import { getMapping, setMapping } from '@pn/core/storage/workspace/mappings';
import { intelMappings } from '@pn/resources/intel-mappings';
import { pnMappings } from '@pn/resources/pn-mappings';
import { pnApiClient } from '@pn/services/api/pnApiClient';
import {
  apiQueryMapper,
  getSqlRowCap,
} from '@pn/services/api/query/apiQueryMapper';
import { isNil, isNumber, isObject } from 'lodash-es';
import { apiDataItemMapper } from './apiDataItemMapper';
import type { ApiDataItem } from './types';

type ApiData = {
  data: ApiDataItem[];
  total_hits: number;
};

export const apiDataProvider: IDataProvider<ApiDataSource> = (dataSource) => ({
  getDataMapping: async (item) => {
    const existingMapping = getMapping(item.dataType);
    if (!isNil(existingMapping)) return existingMapping;

    const mapping =
      dataSource.source === 'parquet'
        ? pnMappings[item.dataType]
        : intelMappings[item.dataType];

    setMapping(item.dataType, mapping);

    return mapping;
  },

  getDataByQuery: async ({ query, mapping }) => {
    const apiQuery = apiQueryMapper(mapping).toTargetQuery(query, true);

    const { data: apiData, total_hits } = await pnApiClient.request<ApiData>({
      method: 'POST',
      url: dataSource.url + '/sql',
      urlTemplate: dataSource.url + '/sql', // preserve :data_type
      payload: {
        query: apiQuery,
        return_global_count: true,
      },
    });

    return {
      data: apiData.map(apiDataItemMapper(mapping).toDomainDataItem),
      totalCount: capCount(total_hits, query.sql),
    };
  },

  streamDataByQuery: async ({ query, mapping }) => {
    const apiQuery = apiQueryMapper(mapping).toTargetQuery(query);

    try {
      const { data: apiData, total_hits } = await pnApiClient.request<ApiData>({
        method: 'POST',
        url: dataSource.url + '/sql',
        urlTemplate: dataSource.url + '/sql', // preserve :data_type
        payload: {
          query: apiQuery,
        },
      });

      return {
        data: apiData.map(apiDataItemMapper(mapping).toDomainDataItem),
        totalCount: capCount(total_hits, query.sql),
      };
    } catch (error) {
      if (isSafeLimitExceeded(error)) {
        return {
          data: [],
          totalCount: capCount(error.responseData.total_hits, query.sql),
          error: String(error),
        };
      }

      throw error;
    }
  },

  getItemById: async ({ id, layerId, mapping }) => {
    const query = {
      ...buildQuery({
        id: layerId,
        dataType: layerId,
        scopeIds: [id],
      }),
      pageSize: 1,
    };
    const apiQuery = apiQueryMapper(mapping).toTargetQuery(query);

    const { data: apiData } = await pnApiClient.request<ApiData>({
      method: 'POST',
      url: dataSource.url + '/sql',
      urlTemplate: dataSource.url + '/sql', // preserve :data_type
      payload: {
        query: apiQuery,
      },
    });

    return apiDataItemMapper(mapping).toDomainDataItem(apiData[0]);
  },
});

/**
 * The API counts every row matching the query's WHERE clause, ignoring an
 * explicit LIMIT in raw SQL, so we cap the count here.
 */
function capCount(totalCount: number, sql: string | undefined): number {
  return Math.min(totalCount, getSqlRowCap(sql) ?? Infinity);
}

function isSafeLimitExceeded(error: unknown): error is ApiError & {
  responseData: { message: string; total_hits: number };
} {
  return (
    error instanceof ApiError &&
    error.code === 400 &&
    isObject(error.responseData) &&
    'total_hits' in error.responseData &&
    isNumber(error.responseData.total_hits)
  );
}
